import { useEffect, useMemo } from 'react'
import { FieldPath, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Group, Stack, Text } from '@mantine/core'
import * as yup from 'yup'

import { AttendeeCard } from '@/components/Event/NewScript/components/common/AttendeeCard/AttendeeCard'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { NotesForm } from '@/components/Event/NewScript/components/common/forms/NotesForm'
import { SpellingsForm } from '@/components/Event/NewScript/components/common/forms/SpellingsForm'
import { TimestampsForm } from '@/components/Event/NewScript/components/common/forms/TimestampsForm'
import { IdentityDocumentCard } from '@/components/Event/NewScript/components/common/IdentityDocumentCard/IdentityDocumentCard'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { AttendeeWithTranscriptFieldCard } from '@/components/Event/NewScript/components/Review/AttendeeWithTranscriptFieldCard'
import { WitnessFieldCard } from '@/components/Event/NewScript/components/Review/WitnessFieldCard'
import { ROLES_WITH_ID } from '@/components/Event/NewScript/constants'
import { withReadWaiveAnswer } from '@/components/Event/NewScript/hooks/state/stateActions'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import {
  EVENT_FORM_CONTEXT_ERRATA_APPLIES,
  EVENT_FORM_CONTEXT_ERRATA_RECIPIENT_ATTENDEE_ID,
  EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS,
  EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED,
  EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED,
  EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED,
} from '@/components/Event/NewScript/schemas'
import { defaultReviewStepSchema } from '@/components/Event/NewScript/schemas/reviewStepSchema'
import {
  getIdentityDocumentExemptAttendeeIds,
  omitPerAttendeeErrors,
} from '@/components/Event/NewScript/utils/attendees'
import {
  describeErrataRecipient,
  doesErrataApply,
} from '@/components/Event/NewScript/utils/errata'
import {
  getWitnessEventTitle,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import { AttendeeRole, ScriptStep } from '@/constants'
import {
  Event,
  EventScriptDataObject,
  ScriptAttendee,
  ScriptData,
} from '@/types'
import { isAttorney } from '@/utils/validations'

type ReviewStepType = yup.InferType<typeof defaultReviewStepSchema>

type DefaultReviewFormValues = {
  timestamps: string | null
  spellings: string | null
  notes: string | null
  attendees: ScriptAttendee[]
}

type DefaultReviewFormProps = {
  event: Event
  state: EventScriptDataObject
  witnessIndex: number
}

export const DefaultReviewForm = ({
  event,
  state,
  witnessIndex,
  ...rest
}: DefaultReviewFormProps) => {
  const { state: globalState, actions } = useScriptState()
  const { isAZ } = useStatePolicies(event)
  const { completeAction, isActionCompleted } = useStepActions(
    ScriptStep.REVIEW,
    event,
  )
  const isEUO = isExaminationUnderOath(event)

  // Stated, not editable: the recipient survives a read -> waive -> read edit
  // here and there is no picker on this screen to check it against.
  const errataApplies = doesErrataApply(state.read_waive_answer)
  const isWitnessLocationRequired = isAZ
  const identityDocumentExemptAttendeeIds =
    getIdentityDocumentExemptAttendeeIds(state.attendees)

  const methods = useForm<DefaultReviewFormValues>({
    mode: 'all',
    resolver: yupResolver(defaultReviewStepSchema),
    context: {
      [EVENT_FORM_CONTEXT_ERRATA_APPLIES]: errataApplies,
      [EVENT_FORM_CONTEXT_ERRATA_RECIPIENT_ATTENDEE_ID]:
        state.errata_recipient_attendee_id,
      [EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED]: true,
      [EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS]:
        identityDocumentExemptAttendeeIds,
      [EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED]: true,
      [EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED]:
        isWitnessLocationRequired,
    },
    defaultValues: {
      timestamps: state.timestamps,
      spellings: state.spellings,
      notes: state.notes,
      attendees: state.attendees,
    },
  })

  const { witness, attorneys, attendees } = useMemo(() => {
    return {
      witness: state.attendees.find(
        attendee => attendee.role === AttendeeRole.WITNESS,
      ),
      attorneys: state.attendees.filter(
        attendee => isAttorney(attendee.role) && attendee.present,
      ),
      attendees: state.attendees,
    }
  }, [state.attendees])

  const syncCurrentEventAttendees = (nextAttendees: ScriptAttendee[]) => {
    actions.updateGlobalState({
      events: (globalState as ScriptData).events.map((event, index) => ({
        ...event,
        attendees: index === witnessIndex ? nextAttendees : event.attendees,
      })),
    })
  }

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name && ['timestamps', 'spellings', 'notes'].includes(name)) {
        return actions.updateGlobalState({
          events: (globalState as ScriptData).events.map((event, index) => ({
            ...event,
            notes: index === witnessIndex ? value.notes : event.notes,
            timestamps:
              index === witnessIndex ? value.timestamps : event.timestamps,
            spellings:
              index === witnessIndex ? value.spellings : event.spellings,
          })),
        })
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, actions])

  const validateAndCompleteAction = async (
    validationKeys: FieldPath<ReviewStepType>[],
    actionKey: string,
  ) => {
    const results = await Promise.all(
      validationKeys.map(key => methods.trigger(key)),
    )

    if (results.every(isValid => isValid)) {
      completeAction(actionKey)
    }
  }

  const witnessName = witness?.name

  return (
    <TimelineAction
      {...rest}
      actionKey={`review-${witnessIndex}`}
      title={getWitnessEventTitle(event, witnessName)}
      onComplete={key =>
        validateAndCompleteAction(
          ['timestamps', 'spellings', 'notes', 'attendees'],
          key,
        )
      }
      completed={isActionCompleted(`review-${witnessIndex}`)}
    >
      <FormProvider {...methods}>
        <ActionContainer outer>
          <Stack>
            <ActionTitle>Attendee information</ActionTitle>
            <Stack>
              {attendees.map((attendee, index) => (
                <ActionContainer key={attendee.attendee_id}>
                  <AttendeeCard
                    onSubmitClick={submittedAttendee => {
                      const formAttendees = methods.getValues('attendees')
                      const nextAttendees = formAttendees.map(formAttendee =>
                        formAttendee.attendee_id ===
                        submittedAttendee.attendee_id
                          ? submittedAttendee
                          : formAttendee,
                      )

                      methods.setValue(`attendees.${index}`, submittedAttendee)
                      syncCurrentEventAttendees(nextAttendees)
                    }}
                    isWithZoomParticipants={false}
                    emailEmptySlot={
                      attendee.transcript_order ? (
                        <Text color="red" italic>
                          fill in email address or remove order
                        </Text>
                      ) : null
                    }
                    event={event}
                    attendee={attendee}
                    namePrefix={`attendees.${index}`}
                    allAttendees={attendees}
                    isWitnessLocationEnabled={isWitnessLocationRequired}
                  />
                </ActionContainer>
              ))}
            </Stack>
          </Stack>
        </ActionContainer>
        <ActionContainer outer>
          <Stack>
            <ActionTitle>Identity documents</ActionTitle>
            {attendees.map((attendee, index) => {
              if (!ROLES_WITH_ID.includes(attendee.role)) {
                return null
              }

              if (
                identityDocumentExemptAttendeeIds.includes(attendee.attendee_id)
              ) {
                return null
              }

              return (
                <IdentityDocumentCard
                  event={event}
                  key={attendee.attendee_id}
                  attendee={attendee}
                  namePrefix={`attendees.${index}.identity_document`}
                  onSubmitClick={submittedAttendee => {
                    const formAttendees = methods.getValues('attendees')
                    const nextAttendees = formAttendees.map(formAttendee =>
                      formAttendee.attendee_id === submittedAttendee.attendee_id
                        ? submittedAttendee
                        : formAttendee,
                    )

                    methods.setValue(`attendees.${index}`, submittedAttendee)
                    syncCurrentEventAttendees(nextAttendees)
                  }}
                />
              )
            })}
          </Stack>
        </ActionContainer>
        <ActionContainer outer>
          <Stack>
            <ActionTitle>Examination information</ActionTitle>
            <ActionContainer>
              <TimestampsForm />
            </ActionContainer>
            <ActionContainer>
              <SpellingsForm />
            </ActionContainer>
            <ActionContainer>
              <NotesForm />
            </ActionContainer>
          </Stack>
        </ActionContainer>
        {/* Transcript information */}
        {!isEUO && (
          <ActionContainer outer>
            <Stack>
              <ActionTitle>Transcript information</ActionTitle>
              {witness && state.read_waive_answer !== 'no_answer' && (
                <WitnessFieldCard
                  witness={witness}
                  answer={state.read_waive_answer}
                  onSubmitClick={value => {
                    actions.updateGlobalState({
                      events: (globalState as ScriptData).events.map(
                        (event, index) =>
                          index === witnessIndex
                            ? withReadWaiveAnswer(event, value)
                            : event,
                      ),
                    })
                  }}
                />
              )}
              {errataApplies && (
                <ActionContainer>
                  <Group spacing="xs">
                    <Text size="sm" fw={500}>
                      Errata recipient
                    </Text>
                    <Text size="sm">
                      {describeErrataRecipient(
                        state.attendees,
                        state.errata_recipient_attendee_id,
                      )}
                    </Text>
                  </Group>
                </ActionContainer>
              )}
              {attorneys.map(attendee => (
                <AttendeeWithTranscriptFieldCard
                  key={attendee.attendee_id}
                  attendee={attendee}
                  onSubmitClick={value => {
                    const formAttendees = methods.getValues('attendees')
                    const attendeeIndex = formAttendees.findIndex(
                      a => a.attendee_id === attendee.attendee_id,
                    )
                    const nextAttendees = formAttendees.map(
                      (formAttendee, i) =>
                        i === attendeeIndex
                          ? {
                              ...formAttendee,
                              transcript_order: value,
                            }
                          : formAttendee,
                    )

                    methods.setValue(
                      `attendees.${attendeeIndex}.transcript_order`,
                      value,
                    )
                    syncCurrentEventAttendees(nextAttendees)
                  }}
                />
              ))}
            </Stack>
          </ActionContainer>
        )}

        {methods.formState.errors && (
          <FormErrors
            errors={omitPerAttendeeErrors(methods.formState.errors)}
          />
        )}
      </FormProvider>
    </TimelineAction>
  )
}
